// src/pages/BasicInfo.js
import React, { useEffect, useMemo, useState } from 'react';
import { InlineLoading, InlineNotification } from '@carbon/react';
import { useParams } from 'react-router-dom';
import CustomButton from '../components/CustomButton';
import PatientUpdateModal from '../components/PatientUpdateModal';
import { useAuth } from '../auth/AuthContext'
import api from "../api";

// 共通スキーマ（DB定義書のキー名・日本語ラベル）
import {
  FIELDS,
  SECTION_GROUPS,
  buildOptionsFromFields,
  labelOf,
  typeOf,
} from '../schema/patientSchema';

/* ================================
 *  既存バックエンド → スキーマキー
 * ================================ */
function mapBackendToSchemaKeys(raw) {
  return {
    research_id:              raw.research_id ?? raw.researchId ?? '',
    consultation_id:          raw.consultation_id ?? raw.patientId ?? '',
    patient_pw:               raw.pw ?? raw.password ?? raw.patient_pw ?? '',
    date_of_birth:            raw.date_of_birth ?? raw.birthDate ?? '',
    gender_type:              raw.gender_type ?? raw.gender ?? '',
    ms_type:                  raw.ms_type ?? '',
    edss_score:               raw.edss_score ?? raw.edssScore ?? '',

    // 病歴
    complications:            raw.complications ?? '',
    medical_history:          raw.medical_history ?? raw.medicalHistory ?? '',
    family_history:           raw.family_history ?? raw.familyHistory ?? '',

    // 生活習慣
    hope_child:               raw.hope_child ?? '',
    smoking:                  raw.smoking ?? '',
    drinking:                 raw.drinking ?? '',

    // 発症時期・様式
    first_detect_date:        raw.first_detect_date ?? '',
    first_detect_way:         raw.first_detect_way ?? '',

    // 初回attack関連
    first_onset_date:         raw.first_onset_date ?? '',
    first_onset_location:     raw.first_onset_location ?? '',
    first_onset_symptom:      raw.first_onset_symptom ?? '',

    // 最終attack関連
    last_onset_date:          raw.last_onset_date ?? '',

    // 攻撃回数・慢性進行
    recurrences_times:        raw.recurrences_times ?? '',
    pira_presence:            raw.pira_presence ?? '',
    pira_suspected_date:      raw.pira_suspected_date ?? '',
    pira_symptoms:           raw.pira_symptoms ?? '',

    // EDSS到達
    last_edss3_date:          raw.last_edss3_date ?? '',
    last_edss6_date:          raw.last_edss6_date ?? '',

    // 検査関連
    aqp4_test:                raw.aqp4_test ?? '',
    mog_test:                 raw.mog_test ?? '',
    antibody_test:            raw.antibody_test ?? '',
    cerebrospinal_fluid_test: raw.cerebrospinal_fluid_test ?? '',
    fluid_test_date:          raw.fluid_test_date ?? '',
    ocb_result_type:          raw.ocb_result_type ?? '',
    fluid_test_company:       raw.fluid_test_company ?? '',
    igg_result_score:         raw.igg_result_score ?? '',

    // date_of_answer は UI に出さない
  };
}

/* ================================
 *  スキーマ → 既存バックエンドへ戻す
 * ================================ */
function applySchemaBackToBackend(vals, oldRaw) {
  return {
    ...oldRaw,    
    research_id:                 vals.research_id,
    consultation_id:             vals.consultation_id,
    password:                    vals.patient_pw, // 既存が password の場合
    birthDate:                   vals.date_of_birth,
    gender:                      vals.gender_type,
    edssScore:                   vals.edss_score,

    // 症状・病歴
    complications:               vals.complications,
    medicalHistory:              vals.medical_history,
    familyHistory:               vals.family_history,
    initialAttackPeriod:         vals.first_onset_date,
    initialAttackLocation:       vals.first_onset_location,
    initialAttackSymptom:        vals.first_onset_symptom,
    msType:                      vals.ms_type,
    attackCountExcludingInitial: vals.recurrences_times,
    disorderSuspectedPeriod:     vals.spms_progression_date,
    chronicProgression:          vals.pira_presence,
    chronicProgressionPeriod:    vals.pira_suspected_date,
    disorderSymptoms:            vals.pira_symptoms,

    // 検査
    aqpResult:                   vals.aqp4_test,
    mogResult:                   vals.mog_test,
    otherAutoantibodies:         vals.antibody_test,
    oligoclonalTest:             vals.cerebrospinal_fluid_test,
    oligoclonalTestPeriod:       vals.fluid_test_date,
    oligoclonalResult:           vals.ocb_result_type,
    oligoclonalCompany:          vals.fluid_test_company,
    iggIndex:                    vals.igg_result_score,
  };
}

/* ================================
 *  表示用フォーマッタ
 * ================================ */
function formatDateLabel(v) {
  if (!v) return '―';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '―';
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

// partialDate（YYYY / YYYY/MM / YYYY/MM/DD or ISO）→ 和式
function formatPartialDateLabel(v) {
  if (!v) return '―';
  const s = String(v).trim();

  // ✅ パターン1: YYYYMM → YYYY年MM月
  if (/^\d{6}$/.test(s)) {
    const y = s.slice(0, 4);
    const m = s.slice(4, 6);
    return `${Number(y)}年${Number(m)}月`;
  }

  // ✅ パターン2: YYYY → YYYY年
  if (/^\d{4}$/.test(s)) return `${s}年`;

  // ✅ パターン3: YYYY/MM または YYYY/MM/DD
  const m = s.replace(/-/g, '/').match(/^(\d{4})\/(\d{1,2})(?:\/(\d{1,2}))?$/);
  if (m) {
    const y = Number(m[1]);
    const mm = Number(m[2]);
    const dd = m[3] ? Number(m[3]) : null;
    return dd ? `${y}年${mm}月${dd}日` : `${y}年${mm}月`;
  }

  // ✅ パターン4: ISO形式
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  }

  return s || '―';
}

/* ================================
 *  セクション・ブロック
 * ================================ */
function SectionBlock({ title, items }) {
  return (
    <section style={{ marginBottom: '2rem' }}>
      <h4 style={{ margin: '1.25rem 0 0.75rem' }}>{title}</h4>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
        {items.map(({ label, value }) => (
          <div
            key={label}
            style={{
              flex: '1 1 45%',
              minWidth: '250px',
              borderBottom: '1px solid #ccc',
              paddingBottom: '0.5rem',
            }}
          >
            <div style={{ fontSize: '0.85rem', color: '#666' }}>{label}</div>
            <div style={{ fontSize: '1rem' }}>{String(value)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ================================
 *  Main
 * ================================ */
export default function BasicInfo() {
  const { patientId } = useParams();
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // 編集モーダル
  const [editOpen, setEditOpen] = useState(false);

  // ロール制御（EP は編集不可）
  const { role, user } = useAuth();
  const canEdit = role && role !== 'expert_physician';

  // enum 選択肢（共通スキーマから）
  const options = useMemo(() => buildOptionsFromFields(), []);

  // データ取得
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);

    const fetchPatientInfo = async () => {
      try {
        // === 患者IDの取得優先順 ===
        const storedPatient = sessionStorage.getItem("patient");
        const patient = storedPatient ? JSON.parse(storedPatient) : null;

        const id = patientId || patient?.research_id || user?.id || "P010";

        const res = await api.get(`/v1/doctor/patient_info/${id}`);
        if (!alive) return;
        setRaw(res.data);
      } catch (e) {
        if (!alive) return;
        setLoadError(`読込エラー：${e.message}`);
        setRaw({});
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchPatientInfo();

    return () => {
      alive = false;
    };
  }, [patientId, user?.id]);

  if (loading) {
    return (
      <div style={{ padding: '2rem' }}>
        <InlineLoading description="読み込み中..." />
      </div>
    );
  }

  // スキーマキーに正規化
  const data = mapBackendToSchemaKeys(raw || {});

  // セクションごとの表示配列を構築
  const sectionToList = (keys) =>
    keys.map((k) => ({
      label: labelOf(k),
      value: (() => {
        const t = typeOf(k);
        if (t === 'date') return formatDateLabel(data[k]);
        if (t === 'partialDate') return formatPartialDateLabel(data[k]);
        return data[k] || '―';
      })(),
    }));

  const infoPersonal = sectionToList(SECTION_GROUPS.personal);
  const infoHistory = sectionToList(SECTION_GROUPS.history);
  const infoLab = sectionToList(SECTION_GROUPS.lab);

  return (
    <>
      <div style={{ maxWidth: 900, margin: '1rem auto' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
            gap: '1rem',
          }}
        >
          <h1 style={{ margin: 0 }}>患者基本情報</h1>

          {/* EP は編集不可：ボタン自体を非表示 */}
          {canEdit && (
            <CustomButton
              kind="primary"
              onClick={() => setEditOpen(true)}
              aria-disabled={!canEdit}
              title="基本情報を編集"
            >
              編集
            </CustomButton>
          )}
        </div>

        {loadError && (
          <div style={{ marginBottom: '1rem' }}>
            <InlineNotification
              kind="error"
              title="読み込みに失敗しました"
              subtitle={loadError}
              lowContrast
            />
          </div>
        )}

        <SectionBlock title="個人情報" items={infoPersonal} />
        <SectionBlock title="症状・病歴" items={infoHistory} />
        <SectionBlock title="検査結果" items={infoLab} />
      </div>

      {/* モーダルはダブルガード（EP では開かない＆保存不可） */}
      <PatientUpdateModal
        open={Boolean(canEdit && editOpen)}
        initialValues={data}
        options={options}
        onClose={() => setEditOpen(false)}
        onSave={async (vals) => {
          if (!canEdit) return; // 念のため

          try {
            // スキーマ形式 → API形式
            const payload = applySchemaBackToBackend(vals, raw);

            // APIが求めるフィールドを埋める（存在しないものは暫定値で埋める）
            const completePayload = {
              consultation_id: payload.consultation_id,
              research_id: payload.research_id,
              pw: payload.password,

              date_of_birth: payload.birthDate,
              gender_type: payload.gender,
              ms_type: payload.msType,

              hope_child: payload.hope_child,
              smoking: payload.smoking,
              drinking: payload.drinking,

              complications: payload.complications,
              medical_history: payload.medicalHistory,
              family_history: payload.familyHistory,

              first_detect_date: payload.first_detect_date,
              first_detect_way: payload.first_detect_way,
              first_onset_date: payload.initialAttackPeriod,
              first_onset_location: payload.initialAttackLocation,
              first_onset_symptom: payload.initialAttackSymptom,
              last_onset_date: payload.last_onset_date,

              recurrences_times: payload.attackCountExcludingInitial,
              pira_presence: payload.chronicProgression,
              pira_suspected_date: payload.chronicProgressionPeriod,
              pira_symptoms: payload.disorderSymptoms,

              last_edss3_date: payload.last_edss3_date,
              last_edss6_date: payload.last_edss6_date,

              aqp4_test: payload.aqpResult,
              mog_test: payload.mogResult,
              antibody_test: payload.otherAutoantibodies,
              cerebrospinal_fluid_test: payload.oligoclonalTest,
              fluid_test_date: payload.oligoclonalTestPeriod,
              ocb_result_type: payload.oligoclonalResult,
              fluid_test_company: payload.oligoclonalCompany,
              igg_result_score: Number(payload.iggIndex),

              date_of_answer: new Date().toISOString().slice(0, 10),
            };

            console.log("送信payload:", completePayload);

            // ✅ POSTリクエストを送信
            const res = await api.post("/v1/doctor/patient_info", completePayload);

            console.log("保存結果:", res.data);
            alert("保存しました！");

            setRaw(payload); // ローカル状態更新
            setEditOpen(false);
          } catch (e) {
            console.error("保存エラー:", e);
            alert("保存に失敗しました");
          }
        }}
      />
    </>
  );
}
